import { memo } from 'react';
import { sameGraphSelection, sameNodeContent } from '../../Services/GraphRenderState';
import { Node, NodeProps, useReactFlow, useStore } from '@xyflow/react';

import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import IconButton from '@mui/material/IconButton';

import useFlowActivityMetadata from '../../Hooks/Metadata/useFlowActivityMetadata';
import { ActivityModel } from '../../Models/FlowModel';
import { FlowEdge, FlowNode } from '../../Models/GraphModel';
import ActivityIcon from '../ActivityIcon';
import { FlowExecutionState, getExecutionTraceStrokeWidth, useFlowExecutionTraceState } from '../Contexts/FlowExecutionTraceContext';
import DisplayContentTemplate from '../DisplayContentTemplate';
import BaseNode from './BaseNode';
import ToolNodeHeader from './ToolNodeHeader';
import CompactAgentServiceIcon from './CompactAgentServiceIcon';
import { COMPACT_AGENT_SERVICE_SIZE, DEFAULT_GROUP_WIDTH } from '../../Services/AgentServiceCompact';
import './GroupNode.scss';

export const GROUP_NODE_TYPE = 'groupTool';

function GroupNode({ data, selected, id }: NodeProps & { data: ActivityModel }) {
	const activityMetadata = useFlowActivityMetadata(data.Type);
	const { getNodes, setEdges, setNodes } = useReactFlow<FlowNode, FlowEdge>();
	const unorderedChildNodes = useStore((store) => (store.nodes as Node<ActivityModel>[]).filter((node) => node.parentId === id), sameGraphSelection);
	const childNodes = orderChildNodes(unorderedChildNodes, data.Content as ActivityModel[] | undefined);
	const executionTrace = useFlowExecutionTraceState();
	const isCollapsed = data.Metadata?.collapsed === true;
	const isCompact = data.Metadata?.compact === true;

	const toggleCollapsed = (event: React.MouseEvent) => {
		event.stopPropagation();
		setNodes((nodes) =>
			nodes.map((node) =>
				node.id === id
					? {
							...node,
							data: {
								...node.data,
								Metadata: {
									...(node.data.Metadata ?? {}),
									collapsed: !isCollapsed,
								},
							},
						}
					: node,
			),
		);
	};

	const selectChildTool = (event: React.SyntheticEvent, childId: string) => {
		event.stopPropagation();
		setEdges((edges) => edges.map((edge) => ({ ...edge, selected: false })));
		setNodes((nodes) => nodes.map((node) => ({ ...node, selected: node.id === childId })));
	};

	const removeChildTool = (event: React.MouseEvent, childId: string) => {
		event.stopPropagation();
		const removedIds = new Set([childId, ...collectDescendantIds(childId, getNodes())]);

		setEdges((edges) => edges.filter((edge) => !removedIds.has(edge.source) && !removedIds.has(edge.target)));
		setNodes((nodes) => renumberGroupChildren(nodes.filter((node) => !removedIds.has(node.id)), id));
	};

	const moveChildTool = (event: React.MouseEvent, childId: string, direction: -1 | 1) => {
		event.stopPropagation();
		const nextOrder = moveChildId(
			childNodes.map((node) => node.id),
			childId,
			direction,
		);
		setNodes((nodes) => applyGroupContentOrder(nodes, id, nextOrder));
	};

	return (
		<BaseNode id={id} data={data} selected={selected} activityMetadata={activityMetadata} nodeClassName={`group-node${isCompact ? ' group-node--compact' : ''}`}>
			{isCompact ? <CompactAgentServiceIcon activity={data} activityMetadata={activityMetadata} /> : <ToolNodeHeader activity={data} activityMetadata={activityMetadata} isCollapsed={isCollapsed} onToggleCollapsed={toggleCollapsed} />}
			{!isCollapsed && !isCompact && (
				<div className="group-node__items">
					{childNodes.map((childNode, index) => (
						<GroupToolItem
							key={childNode.id}
							activity={childNode.data}
							selected={childNode.selected === true}
							executionState={executionTrace?.nodeStates.get(childNode.id)}
							visitCount={executionTrace?.nodeVisitCounts.get(childNode.id) ?? 1}
							canMoveUp={index > 0}
							canMoveDown={index < childNodes.length - 1}
							onSelect={(event) => selectChildTool(event, childNode.id)}
							onMoveUp={(event) => moveChildTool(event, childNode.id, -1)}
							onMoveDown={(event) => moveChildTool(event, childNode.id, 1)}
							onRemove={(event) => removeChildTool(event, childNode.id)}
						/>
					))}
				</div>
			)}
		</BaseNode>
	);
}

function GroupToolItem({
	activity,
	selected,
	executionState,
	visitCount,
	canMoveUp,
	canMoveDown,
	onSelect,
	onMoveUp,
	onMoveDown,
	onRemove,
}: {
	activity: ActivityModel;
	selected: boolean;
	executionState?: FlowExecutionState;
	visitCount: number;
	canMoveUp: boolean;
	canMoveDown: boolean;
	onSelect: (event: React.SyntheticEvent) => void;
	onMoveUp: (event: React.MouseEvent) => void;
	onMoveDown: (event: React.MouseEvent) => void;
	onRemove: (event: React.MouseEvent) => void;
}) {
	const activityMetadata = useFlowActivityMetadata(activity.Type);

	return (
		<div
			className={[
				'group-node__item',
				selected ? 'group-node__item--selected' : '',
				executionState === 'active' ? 'group-node__item--trace-active' : executionState === 'visited' ? 'group-node__item--trace-visited' : '',
			]
				.filter(Boolean)
				.join(' ')}
			role="button"
			style={executionState ? { borderWidth: `${getExecutionTraceStrokeWidth(visitCount)}px` } : undefined}
			tabIndex={0}
			onClick={onSelect}
			onMouseDown={(event) => event.stopPropagation()}
			onKeyDown={(event) => {
				if (event.key === 'Enter' || event.key === ' ') {
					event.preventDefault();
					onSelect(event);
				}
			}}
		>
			<ActivityIcon activityMetadata={activityMetadata} />
			<DisplayContentTemplate
				template={activityMetadata?.displayContent}
				fallbackText={activityMetadata?.displayName ?? activity.Type}
				context={activity}
				className="group-node__item-label"
			/>
			<div className="group-node__item-order-actions">
				<IconButton
					className="group-node__item-order"
					title="Move up"
					size="small"
					disabled={!canMoveUp}
					onClick={onMoveUp}
					onMouseDown={(event) => event.stopPropagation()}
				>
					<KeyboardArrowUpIcon fontSize="inherit" />
				</IconButton>
				<IconButton
					className="group-node__item-order"
					title="Move down"
					size="small"
					disabled={!canMoveDown}
					onClick={onMoveDown}
					onMouseDown={(event) => event.stopPropagation()}
				>
					<KeyboardArrowDownIcon fontSize="inherit" />
				</IconButton>
			</div>
			<IconButton className="group-node__item-remove" title="Remove" size="small" onClick={onRemove} onMouseDown={(event) => event.stopPropagation()}>
				<DeleteIcon fontSize="inherit" />
			</IconButton>
		</div>
	);
}

function collectDescendantIds(rootId: string, nodes: Node<ActivityModel>[]): string[] {
	const descendants: string[] = [];
	const pending = [rootId];

	while (pending.length > 0) {
		const currentId = pending.pop();
		const childIds = nodes.filter((node) => node.parentId === currentId).map((node) => node.id);
		descendants.push(...childIds);
		pending.push(...childIds);
	}

	return descendants;
}

function orderChildNodes(childNodes: Node<ActivityModel>[], content: ActivityModel[] | undefined): Node<ActivityModel>[] {
	const childById = new Map(childNodes.map((node) => [node.id, node]));
	const orderedIds = Array.isArray(content) ? content.map((activity) => activity.Id).filter((childId) => childById.has(childId)) : [];
	const unorderedIds = childNodes.map((node) => node.id).filter((childId) => !orderedIds.includes(childId));
	return [...orderedIds, ...unorderedIds].map((childId) => childById.get(childId)).filter((node): node is Node<ActivityModel> => node != null);
}

function moveChildId(childIds: string[], childId: string, direction: -1 | 1): string[] {
	const fromIndex = childIds.indexOf(childId);
	const toIndex = fromIndex + direction;
	if (fromIndex < 0 || toIndex < 0 || toIndex >= childIds.length) return childIds;

	const nextOrder = [...childIds];
	const [movedChildId] = nextOrder.splice(fromIndex, 1);
	nextOrder.splice(toIndex, 0, movedChildId);
	return nextOrder;
}

function applyGroupContentOrder(nodes: FlowNode[], groupId: string, orderedChildIds: string[]): FlowNode[] {
	return nodes.map((node) => {
		if (node.id !== groupId) return node;

		const childById = new Map(nodes.filter((candidate) => candidate.parentId === groupId).map((child) => [child.id, child]));
		const orderedContent = orderedChildIds.map((childId) => childById.get(childId)?.data).filter((activity): activity is ActivityModel => activity != null);

		return {
			...node,
			data: {
				...node.data,
				Content: orderedContent,
			},
		};
	});
}

function renumberGroupChildren(nodes: FlowNode[], groupId: string): FlowNode[] {
	const groupNode = nodes.find((node) => node.id === groupId);
	const groupContent = (groupNode?.data as ActivityModel | undefined)?.Content as ActivityModel[] | undefined;
	const orderedChildren = orderChildNodes(nodes.filter((node) => node.parentId === groupId) as Node<ActivityModel>[], groupContent);
	return applyGroupContentOrder(
		nodes,
		groupId,
		orderedChildren.map((node) => node.id),
	);
}

export function nodeFromGroup(model: ActivityModel, x: number = 0, y: number = 0): Node<ActivityModel> {
	return {
		id: model.Id,
		type: GROUP_NODE_TYPE,
		position: { x, y },
		data: model,
		width: model.Metadata?.compact === true ? COMPACT_AGENT_SERVICE_SIZE : DEFAULT_GROUP_WIDTH,
	} as Node<ActivityModel>;
}

export default memo(GroupNode, sameNodeContent);
